import React, { useState } from 'react';
import { Github, ExternalLink, Play, Code, BarChart3, Calendar, Award } from 'lucide-react';
import { projects, codeSnippets } from '../data/mock';

const Projects = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showDemo, setShowDemo] = useState(null);
  const [showNotebook, setShowNotebook] = useState(null);

  const featuredProjects = projects.filter(project => project.featured);
  const displayProjects = activeTab === 'all' ? projects : featuredProjects;

  const DemoModal = ({ project, onClose }) => (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="glass-modal rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[rgba(255,255,255,0.1)]">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-white">Interactive Demo: {project.title}</h3>
            <button 
              onClick={onClose} 
              className="glass-light w-10 h-10 rounded-xl flex items-center justify-center text-[rgb(161,161,170)] hover:text-[rgb(218,255,1)] hover:border-[rgba(218,255,1,0.3)] transition-all duration-300"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="p-6">
          <div className="glass-card rounded-xl p-6">
            <div className="text-center space-y-4">
              <div className="w-24 h-24 mx-auto glass-light rounded-full flex items-center justify-center group">
                <Play size={48} className="text-[rgb(218,255,1)] group-hover:scale-110 transition-transform duration-300" />
              </div>
              <h4 className="text-lg font-semibold text-white">Football Highlights Demo</h4>
              <p className="text-[rgb(161,161,170)]">Upload a football match video to automatically generate highlights</p>
              <div className="glass-light rounded-lg p-4 border-2 border-dashed border-[rgba(218,255,1,0.3)]">
                <p className="text-sm text-[rgb(161,161,170)] mb-2">Drag and drop video file or click to browse</p>
                <button className="glass-button text-[rgb(17,17,19)] px-4 py-2 rounded-lg font-medium transition-all duration-300 hover:shadow-[0_0_20px_rgba(218,255,1,0.4)]">
                  Select Video File
                </button>
              </div>
              <div className="text-left space-y-2 glass-light rounded-xl p-4">
                <h5 className="font-semibold text-white">Demo Features:</h5>
                <ul className="text-sm text-[rgb(161,161,170)] space-y-1">
                  <li>• Real-time event detection (goals, cards, fouls)</li>
                  <li>• Audio intensity analysis for crowd reactions</li>
                  <li>• Customizable highlight duration</li>
                  <li>• Automatic video compilation</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const NotebookModal = ({ project, onClose }) => {
    const code = project.id === 1 ? codeSnippets.footballHighlights : codeSnippets.cropDisease;
    
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="glass-modal rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-[rgba(255,255,255,0.1)]">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">Code Notebook: {project.title}</h3>
              <button 
                onClick={onClose}
                className="glass-light w-10 h-10 rounded-xl flex items-center justify-center text-[rgb(161,161,170)] hover:text-[rgb(218,255,1)] hover:border-[rgba(218,255,1,0.3)] transition-all duration-300"
              >
                ✕
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="glass-card rounded-xl">
              {/* Notebook Header */}
              <div className="flex items-center space-x-2 p-4 border-b border-[rgba(255,255,255,0.1)] glass-light rounded-t-xl">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="ml-4 text-sm text-[rgb(161,161,170)]">Jupyter Notebook</span>
              </div>
              
              {/* Code Content */}
              <div className="p-6">
                <pre className="text-sm text-[rgb(218,218,218)] glass-light rounded-lg p-4 overflow-x-auto">
                  <code>{code}</code>
                </pre>
                
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="glass-light rounded-lg p-4">
                    <h5 className="font-semibold text-white mb-2">Key Metrics</h5>
                    <div className="space-y-1 text-sm">
                      {Object.entries(project.metrics).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-[rgb(161,161,170)] capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                          <span className="text-[rgb(218,255,1)]">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="glass-light rounded-lg p-4">
                    <h5 className="font-semibold text-white mb-2">Tech Stack</h5>
                    <div className="flex flex-wrap gap-1">
                      {project.tech.slice(0, 4).map((tech, i) => (
                        <span key={i} className="px-2 py-1 bg-[rgba(218,255,1,0.1)] text-[rgb(218,255,1)] text-xs rounded border border-[rgba(218,255,1,0.2)]">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="glass-light rounded-lg p-4">
                    <h5 className="font-semibold text-white mb-2">Actions</h5>
                    <div className="space-y-2">
                      <button className="w-full glass-button text-[rgb(17,17,19)] py-2 px-3 rounded text-sm font-medium transition-all duration-300">
                        Run in Colab
                      </button>
                      <button className="w-full glass-light text-[rgb(218,218,218)] py-2 px-3 rounded text-sm hover:border-[rgba(218,255,1,0.3)] hover:text-[rgb(218,255,1)] transition-all duration-300">
                        View on GitHub
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section id="projects" className="py-20 bg-gradient-to-br from-[rgb(17,17,19)] to-[rgb(26,28,30)] relative overflow-hidden">
      {/* Glassmorphism background elements */}
      <div className="absolute top-20 right-20 w-48 h-48 rounded-full bg-gradient-to-br from-[rgba(218,255,1,0.1)] to-transparent backdrop-blur-sm opacity-40"></div>
      <div className="absolute bottom-40 left-20 w-36 h-36 rounded-full bg-gradient-to-tl from-[rgba(218,255,1,0.08)] to-transparent backdrop-blur-sm opacity-30"></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Section Header with glass effect */}
          <div className="text-center mb-16">
            <div className="inline-block glass-light px-6 py-2 rounded-full mb-6">
              <span className="text-[rgb(218,255,1)] text-sm font-medium tracking-wide">MY WORK</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 relative">
              <span className="relative z-10">
                Featured <span className="text-[rgb(218,255,1)] relative">
                  Projects
                  <div className="absolute inset-0 bg-[rgb(218,255,1)] opacity-20 blur-xl rounded-lg"></div>
                </span>
              </span>
            </h2>
            <p className="text-xl text-[rgb(161,161,170)] max-w-3xl mx-auto leading-relaxed">
              A showcase of AI/ML projects spanning computer vision, deep learning, and full-stack development
            </p>
          </div>

          {/* Filter Tabs with glassmorphism */}
          <div className="flex justify-center mb-12">
            <div className="glass-card rounded-2xl p-2">
              <button
                onClick={() => setActiveTab('all')}
                className={`px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                  activeTab === 'all'
                    ? 'glass-button text-[rgb(17,17,19)]'
                    : 'text-[rgb(161,161,170)] hover:text-[rgb(218,255,1)] hover:bg-[rgba(218,255,1,0.1)]'
                }`}
              >
                All Projects ({projects.length})
              </button>
              <button
                onClick={() => setActiveTab('featured')}
                className={`px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                  activeTab === 'featured'
                    ? 'glass-button text-[rgb(17,17,19)]'
                    : 'text-[rgb(161,161,170)] hover:text-[rgb(218,255,1)] hover:bg-[rgba(218,255,1,0.1)]'
                }`}
              >
                Featured ({featuredProjects.length})
              </button>
            </div>
          </div>

          {/* Projects Grid with enhanced glassmorphism */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {displayProjects.map((project) => (
              <div
                key={project.id}
                className="group glass-card rounded-2xl overflow-hidden hover:glass-card hover:transform hover:translate-y-[-4px] hover:shadow-[0_20px_40px_rgba(0,0,0,0.4)] transition-all duration-300 relative"
              >
                {/* Top accent glow */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[rgb(218,255,1)] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="p-8">
                  {/* Project Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-white group-hover:text-[rgb(218,255,1)] transition-colors duration-300">
                          {project.title}
                        </h3>
                        {project.featured && (
                          <div className="glass-light w-8 h-8 rounded-full flex items-center justify-center">
                            <Award size={16} className="text-[rgb(218,255,1)]" />
                          </div>
                        )}
                      </div>
                      <div className="flex items-center text-sm text-[rgb(161,161,170)] mb-3">
                        <Calendar size={14} className="mr-1" />
                        {project.period}
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-[rgb(218,218,218)] mb-6 leading-relaxed">
                    {project.description}
                  </p>

                  {/* Highlights */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-white mb-3">Key Achievements:</h4>
                    <ul className="space-y-2">
                      {project.highlights.map((highlight, i) => (
                        <li key={i} className="flex items-start space-x-2 text-sm text-[rgb(161,161,170)]">
                          <span className="text-[rgb(218,255,1)] font-bold">•</span>
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Tech Stack with glass effect */}
                  <div className="mb-6">
                    <div className="flex flex-wrap gap-2">
                      {project.tech.map((tech, i) => (
                        <span
                          key={i}
                          className="glass-light px-3 py-1 text-[rgb(218,218,218)] rounded-lg text-sm font-medium hover:border-[rgba(218,255,1,0.3)] hover:text-[rgb(218,255,1)] transition-all duration-200"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Metrics with glassmorphism */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {Object.entries(project.metrics).map(([key, value]) => (
                      <div key={key} className="text-center glass-light rounded-xl p-3">
                        <div className="text-lg font-bold text-[rgb(218,255,1)]">{value}</div>
                        <div className="text-xs text-[rgb(161,161,170)] capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Action Buttons with glass effect */}
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 glass-light hover:border-[rgba(218,255,1,0.3)] text-[rgb(218,218,218)] hover:text-[rgb(218,255,1)] px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300"
                    >
                      <Github size={16} />
                      GitHub
                    </a>
                    
                    {project.hasNotebook && (
                      <button
                        onClick={() => setShowNotebook(project)}
                        className="flex items-center gap-2 glass-light hover:border-[rgba(218,255,1,0.3)] text-[rgb(218,218,218)] hover:text-[rgb(218,255,1)] px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300"
                      >
                        <Code size={16} />
                        View Code
                      </button>
                    )}
                    
                    {project.hasDemo && (
                      <button
                        onClick={() => setShowDemo(project)}
                        className="flex items-center gap-2 glass-button text-[rgb(17,17,19)] px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:shadow-[0_0_20px_rgba(218,255,1,0.3)]"
                      >
                        <Play size={16} />
                        Live Demo
                      </button>
                    )}
                  </div>
                </div>

                {/* Subtle shimmer effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[rgba(218,255,1,0.05)] to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-out pointer-events-none"></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showDemo && <DemoModal project={showDemo} onClose={() => setShowDemo(null)} />}
      {showNotebook && <NotebookModal project={showNotebook} onClose={() => setShowNotebook(null)} />}
    </section>
  );
};

export default Projects;